import React from "react";
import { ClipboardList, Pencil, Trash2 } from "lucide-react";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import useProductUpdates from "../hooks/useProductUpdates.js";
import useAccess from "../hooks/useAccess.jsx";
import { ACCESS_PERMISSION_KEYS, hasPermission } from "../lib/accessControl.js";
import { formatPublicationDate, productUpdatesRequest } from "../lib/productUpdatesApi.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import ProductUpdatePublicationView from "./ProductUpdatePublicationView.jsx";
import ProductUpdatesAdmin from "./ProductUpdatesAdmin.jsx";
import ProductUpdatePublishedEditor from "./ProductUpdatePublishedEditor.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";

const PAGE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("updates");

export default function UpdatesPage() {
  const { publications, error, reloadPublished } = useProductUpdates({ loadPublished: true });
  const { access } = useAccess();
  const canManage = hasPermission(access, ACCESS_PERMISSION_KEYS.productUpdatesManage);
  const [localActionsAvailable, setLocalActionsAvailable] = React.useState(false);
  const canEdit = canManage && localActionsAvailable;
  const [view, setView] = React.useState("journal");
  const [editing, setEditing] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [discardAction, setDiscardAction] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [actionError, setActionError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [syncTarget, setSyncTarget] = React.useState(null);
  const deleteBusy = React.useRef(false);
  const deleteTrigger = React.useRef(null);
  const sectionRef = React.useRef(null);
  React.useEffect(() => {
    let active = true;
    setLocalActionsAvailable(false);
    if (canManage) productUpdatesRequest("/catalog").then(result => {
      if (active) setLocalActionsAvailable(result.local_test_available === true);
    }).catch(() => {});
    return () => { active = false; };
  }, [canManage]);
  const showPreparation = canManage && view === "preparation";
  const visible = editing && !publications.some(row => row.id === editing.id) ? [editing, ...publications] : publications;
  function leaveEditor(action) {
    if (busy) return;
    if (dirty) { setDiscardAction(() => action); return; }
    setEditing(null); setDirty(false); action();
  }
  function cancelDelete() {
    setDeleteTarget(null);
    if (deleteTrigger.current?.isConnected) deleteTrigger.current.focus();
    else sectionRef.current?.focus();
  }
  async function remove() {
    if (deleteBusy.current || !canEdit) return;
    deleteBusy.current = true; setBusy(true); setActionError("");
    try {
      const result = await productUpdatesRequest(`/publications/${deleteTarget.id}`, { method: "DELETE", body: { version: deleteTarget.version } });
      setNotice(result.deployment_sync_error ? "La publication a été supprimée en local." : "La publication a été supprimée. La suppression est préparée pour le prochain déploiement.");
      setActionError(result.deployment_sync_error || ""); cancelDelete();
      setSyncTarget(result.deployment_sync_error ? result : null);
      await reloadPublished();
    } catch (failure) {
      setActionError(failure.message);
      if (failure.status === 409 || failure.status === 404) { cancelDelete(); await reloadPublished(); }
    } finally { deleteBusy.current = false; setBusy(false); }
  }
  async function retrySync() {
    if (busy || !syncTarget || !canEdit) return;
    setBusy(true);
    try {
      await productUpdatesRequest(`/publications/${syncTarget.id}/sync`, { method: "POST", body: { version: syncTarget.version } });
      setSyncTarget(null); setActionError(""); setNotice("Le changement est prêt pour le prochain déploiement.");
    } catch (failure) { setActionError(failure.message); } finally { setBusy(false); }
  }
  return <div className={`mx-auto grid w-full ${PAGE_WIDTH_CLASS_NAME} gap-3 px-4 pb-6 pt-0 max-[720px]:gap-2.5`}>
    <WorkspacePageHeader truckBanner icon={ClipboardList} title="Journal" description="Les nouveautés de TransIA, par sujet et du plus récent au plus ancien." />
    {canManage && <div role="group" aria-label="Vues du Journal" className="flex flex-wrap gap-2">
      <button type="button" className={`pu-button${!showPreparation ? " pu-button--primary" : ""}`} aria-pressed={!showPreparation}
        aria-controls="journal-publications" disabled={busy} onClick={() => setView("journal")}>Journal</button>
      <button type="button" className={`pu-button${showPreparation ? " pu-button--primary" : ""}`} aria-pressed={showPreparation}
        aria-controls="journal-preparation" disabled={busy} onClick={() => leaveEditor(() => setView("preparation"))}>Préparer une publication</button>
    </div>}
    {canManage && <div id="journal-preparation" hidden={!showPreparation}>
      <ProductUpdatesAdmin onChanged={reloadPublished} />
    </div>}
    <section id="journal-publications" aria-label="Publications" hidden={showPreparation} ref={sectionRef} tabIndex={-1}>
    {error && <p role="alert" className="pu-error">{error}</p>}
    {actionError && <p role="alert" className="pu-error">{actionError}</p>}
    {canEdit && syncTarget && <button type="button" className="pu-button" disabled={busy} onClick={() => void retrySync()}>Réessayer la préparation du déploiement</button>}
    {notice && <p role="status" className="text-sm text-slate-700">{notice}</p>}
    <div className="grid gap-2">
      {visible.map(publication => <article key={publication.id} className="grid min-w-0 gap-1.5 rounded-[4px] border border-slate-200 bg-white p-2">
        <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{formatPublicationDate(publication.published_at)}</h2>
        {canEdit && <div className="pu-actions">
          <button type="button" className="pu-button pu-journal-icon" title="Modifier la publication" aria-label="Modifier la publication" disabled={busy || editing?.id === publication.id}
            onClick={() => leaveEditor(() => { setEditing(publication); setActionError(""); setNotice(""); })}><Pencil size={16} aria-hidden="true" /></button>
          <button type="button" className="pu-button pu-button--danger pu-journal-icon" title="Supprimer la publication" aria-label="Supprimer la publication" disabled={busy}
            onClick={event => { const trigger = event.currentTarget; leaveEditor(() => { deleteTrigger.current = trigger; setDeleteTarget(publication); setActionError(""); }); }}><Trash2 size={16} aria-hidden="true" /></button>
        </div>}</div>
        {canEdit && editing?.id === publication.id ? <ProductUpdatePublishedEditor key={editing.id} publication={editing}
          onDirtyChange={setDirty} onBusyChange={setBusy} onClose={() => leaveEditor(() => {})}
          onSaved={result => { setEditing(null); setDirty(false); setNotice(result.deployment_sync_error ? "La publication a été enregistrée en local." : "La publication a été enregistrée. La correction est préparée pour le prochain déploiement."); setActionError(result.deployment_sync_error || ""); setSyncTarget(result.deployment_sync_error ? result : null); void reloadPublished(); }} />
          : <ProductUpdatePublicationView content={publication.content_json} compact />}
      </article>)}
    </div></section>
    {discardAction && <ConfirmationModal title="Abandonner les modifications ?" tone="warning" panelSize="narrow" cornerRadius="grain" summaryLayout="stack"
      description="Les modifications non enregistrées de cette publication seront perdues." confirmLabel="Abandonner"
      onConfirm={() => { const action = discardAction; setDiscardAction(null); setEditing(null); setDirty(false); action(); }}
      onCancel={() => setDiscardAction(null)} onClose={() => setDiscardAction(null)} />}
    {deleteTarget && <ConfirmationModal title="Supprimer cette publication ?" tone="danger" panelSize="narrow" cornerRadius="grain" summaryLayout="stack"
      description="Toute cette publication sera supprimée du Journal et des annonces. Ses textes déjà diffusés seront conservés."
      summaryItems={[{ label: "Publication", value: formatPublicationDate(deleteTarget.published_at) }]}
      confirmLabel={busy ? "Suppression…" : "Supprimer"} isBusy={busy}
      warningMessage={actionError || undefined} onConfirm={() => void remove()} onCancel={cancelDelete} onClose={cancelDelete} />}
  </div>;
}
